<template>
  <div class="page-view">

    <BasePageHeader :title="$t('documents.title')" @back="$router.back()">
      <template #actions>
        <button class="btn-icon" @click="load" :disabled="loading" :title="$t('documents.refresh')">
          <MdiRefresh class="w-[22px] h-[22px]" />
        </button>
      </template>
    </BasePageHeader>

    <!-- Filters -->
    <div class="px-4 py-3 flex flex-col gap-2 border-b border-[var(--color-border)]">
      <!-- Row 1: Search -->
      <SearchField v-model="search" :debounce="0" :placeholder="$t('documents.searchPlaceholder')" />

      <!-- Row 2: selects -->
      <div class="flex gap-2 flex-wrap">
        <BaseSelect v-model="docTypeFilter" density="compact" class="flex-1 min-w-[9rem]" :aria-label="$t('documents.allTypes')">
          <option value="">{{ $t('documents.allTypes') }}</option>
          <option v-for="dtype in DOC_TYPES" :key="dtype.value" :value="dtype.value">{{ dtype.label }}</option>
        </BaseSelect>
        <BaseSelect v-model="projectStatusFilter" density="compact" class="flex-1 min-w-[9rem]" :aria-label="$t('documents.allStatuses')">
          <option value="">{{ $t('documents.allStatuses') }}</option>
          <option v-for="s in PROJECT_STATUSES" :key="s.value" :value="s.value">{{ s.label }}</option>
        </BaseSelect>
        <BaseSelect v-model="isSignedFilter" density="compact" class="flex-1 min-w-[8rem]" :aria-label="$t('documents.signedAndUnsigned')">
          <option value="">{{ $t('documents.signedAndUnsigned') }}</option>
          <option value="true">{{ $t('documents.signedOnly') }}</option>
          <option value="false">{{ $t('documents.unsignedOnly') }}</option>
        </BaseSelect>
      </div>

      <!-- Row 3: result count + ordering -->
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs text-[var(--color-muted)]">
          <template v-if="!loading">{{ totalCount }} {{ totalCount !== 1 ? $t('documents.documentsPlural') : $t('documents.documentSingular') }}</template>
          <template v-else>{{ $t('documents.loading') }}</template>
        </span>
        <div class="flex items-center gap-1.5">
          <span class="text-xs text-[var(--color-muted)]">{{ $t('documents.sort') }}</span>
          <BaseSelect v-model="ordering" density="compact" class="w-auto min-w-0 text-[0.78rem]" :aria-label="$t('documents.sort')">
            <option value="-created_at">{{ $t('documents.sortNewest') }}</option>
            <option value="created_at">{{ $t('documents.sortOldest') }}</option>
            <option value="document_type">{{ $t('documents.sortTypeAZ') }}</option>
            <option value="-document_type">{{ $t('documents.sortTypeZA') }}</option>
            <option value="project_name">{{ $t('documents.sortProjectAZ') }}</option>
            <option value="-project_name">{{ $t('documents.sortProjectZA') }}</option>
          </BaseSelect>
        </div>
      </div>
    </div>

    <EmptyState v-if="loading && !documents.length" :message="$t('documents.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />
    <EmptyState v-else-if="!documents.length" :message="$t('documents.noDocuments')" />

    <div v-else class="list-stack">
      <div
        v-for="doc in documents"
        :key="doc.id"
        class="data-card"
      >
        <div class="data-card__main">
          <div class="flex items-center gap-2 mb-1">
            <component :is="docTypeIcon(doc.document_type)" class="w-4 h-4 shrink-0" :class="docTypeIconClass(doc.document_type)" />
            <div class="data-card__name">{{ doc.title }}</div>
            <span v-if="doc.is_signed" class="badge badge-success">{{ $t('documents.signed') }}</span>
          </div>

          <div class="data-card__meta">
            <span v-if="doc.project_name" class="badge badge-pill" style="background:#1e3a5f;color:#93c5fd;">
              <MdiFolder class="w-[11px] h-[11px] shrink-0" />
              {{ doc.project_name }}
            </span>
            <span class="badge badge-muted">{{ doc.document_type_display }}</span>
            <span class="badge badge-pill badge-muted">{{ docNumber(doc) }}</span>
          </div>

          <div class="flex flex-wrap gap-3 mt-1.5">
            <span class="flex flex-col gap-0.5 text-[.8rem] text-[var(--color-muted)]">
              <span class="text-[.7rem] uppercase tracking-[.04em]">{{ $t('documents.created') }}</span>
              <span style="color: var(--color-text);">{{ formatDate(doc.created_at) }}</span>
            </span>
            <span v-if="doc.created_by_email" class="flex flex-col gap-0.5 text-[.8rem] text-[var(--color-muted)]">
              <span class="text-[.7rem] uppercase tracking-[.04em]">{{ $t('documents.by') }}</span>
              <span style="color: var(--color-text);">{{ doc.created_by_email }}</span>
            </span>
          </div>
        </div>

        <div class="data-card__actions" @click.stop>
          <button
            class="btn-icon"
            :title="$t('documents.previewTitle')"
            @click="openPreview(doc)"
          >
            <MdiEye class="w-[18px] h-[18px]" />
          </button>
          <a
            :href="doc.download_url"
            target="_blank"
            rel="noopener"
            class="btn-icon"
            :title="$t('documents.downloadTitle')"
          >
            <MdiDownload class="w-[18px] h-[18px]" />
          </a>
          <BaseIconButton variant="danger" :title="$t('documents.deleteTitle')" @click="confirmDelete(doc)">
            <MdiTrashCan class="w-[18px] h-[18px]" />
          </BaseIconButton>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center gap-3 px-4 py-4">
      <button class="btn ghost small" :disabled="currentPage === 1" @click="currentPage--">{{ $t('documents.prevPage') }}</button>
      <span class="text-xs text-[var(--color-muted)]">{{ $t('documents.pageOf', { current: currentPage, total: totalPages }) }}</span>
      <button class="btn ghost small" :disabled="currentPage === totalPages" @click="currentPage++">{{ $t('documents.nextPage') }}</button>
    </div>

    <BaseConfirmModal
      :show="!!deleteTarget"
      :title="$t('documents.deleteConfirmTitle')"
      :body="deleteTarget ? $t('documents.deleteConfirmBody', { name: escapeHtml(deleteTarget.title) }) : ''"
      :confirm-label="$t('common.delete')"
      :loading="deleting"
      @confirm="doDelete"
      @cancel="deleteTarget = null"
    />

    <PdfPreviewModal
      :show="previewOpen"
      :url="previewUrl"
      :title="previewTitle"
      :download-name="previewDownloadName"
      @close="closePreview"
    />

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { getDocuments, deleteDocument } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import BaseIconButton from '@/components/base/BaseIconButton.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import PdfPreviewModal from '@/components/base/PdfPreviewModal.vue'
import SearchField from '@/components/search/SearchField.vue'
import MdiRefresh from '~icons/mdi/refresh'
import MdiDownload from '~icons/mdi/download'
import MdiEye from '~icons/mdi/eye'
import MdiTrashCan from '~icons/mdi/trash-can'
import MdiFolder from '~icons/mdi/folder'
import MdiFileDocument from '~icons/mdi/file-document'
import MdiFileSign from '~icons/mdi/file-sign'
import MdiClipboardList from '~icons/mdi/clipboard-list'
import MdiHandshake from '~icons/mdi/handshake'

const { t } = useI18n()

const DOC_TYPES = computed(() => [
  { value: 'offer', label: t('documents.docTypeOffer') },
  { value: 'picking_list', label: t('documents.docTypePickingList') },
  { value: 'contract', label: t('documents.docTypeContract') },
  { value: 'signed_contract', label: t('documents.docTypeSignedContract') },
])

const DOC_PREFIXES = {
  offer:           'TILBUD',
  picking_list:    'PLUKK',
  contract:        'KONTRAKT',
  signed_contract: 'SIGNERT',
}

// Reverse map: prefix (lowercase) → document_type value
const PREFIX_TO_TYPE = Object.fromEntries(
  Object.entries(DOC_PREFIXES).map(([t, p]) => [p.toLowerCase(), t])
)

const DOC_NUMBER_RE = /^([A-Za-z]+)(?:-(\d+))?(?:-(\d+))?$/

function docNumber(doc) {
  const prefix = DOC_PREFIXES[doc.document_type] ?? 'DOK'
  if (!doc.project) return `${prefix}-${doc.id}`
  return `${prefix}-${doc.project}-${doc.id}`
}

// Returns parsed segments if the term looks like a doc number, null otherwise.
function parseDocNumberSearch(term) {
  const m = term.trim().match(DOC_NUMBER_RE)
  if (!m) return null
  const docType = PREFIX_TO_TYPE[m[1].toLowerCase()]
  if (!docType) return null
  return {
    docType,
    projectId: m[2] ? parseInt(m[2], 10) : null,
    docId:     m[3] ? parseInt(m[3], 10) : null,
  }
}

const PROJECT_STATUSES = computed(() => [
  { value: 'draft', label: t('documents.statusDraft') },
  { value: 'confirmed', label: t('documents.statusConfirmed') },
  { value: 'ready', label: t('documents.statusReady') },
  { value: 'picked_up', label: t('documents.statusPickedUp') },
  { value: 'delivered', label: t('documents.statusDelivered') },
  { value: 'active', label: t('documents.statusActive') },
  { value: 'returning', label: t('documents.statusReturning') },
  { value: 'completed', label: t('documents.statusCompleted') },
  { value: 'cancelled', label: t('documents.statusCancelled') },
])

const PAGE_SIZE = 20

const documents  = ref([])
const totalCount = ref(0)
const loading    = ref(false)
const loadError  = ref(null)
const deleting   = ref(false)
const deleteTarget = ref(null)

const previewOpen         = ref(false)
const previewUrl          = ref(null)
const previewTitle        = ref('')
const previewDownloadName = ref('document.pdf')

function openPreview(doc) {
  previewTitle.value        = doc.title || doc.document_type_display
  previewDownloadName.value = `${doc.title || doc.document_type_display}.pdf`
  previewUrl.value          = doc.download_url
  previewOpen.value         = true
}

function closePreview() {
  previewOpen.value = false
  previewUrl.value  = null
}

const search             = ref('')
const docTypeFilter      = ref('')
const projectStatusFilter = ref('')
const isSignedFilter     = ref('')
const ordering           = ref('-created_at')
const currentPage        = ref(1)

const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / PAGE_SIZE)))

function buildParams() {
  const p = { page: currentPage.value, page_size: PAGE_SIZE, ordering: ordering.value }
  const rawSearch = search.value.trim()
  if (rawSearch) {
    const parsed = parseDocNumberSearch(rawSearch)
    if (parsed) {
      // Translate structured doc number to API filter params
      if (parsed.docType && !docTypeFilter.value) p.document_type = parsed.docType
      if (parsed.projectId) p.project = parsed.projectId
      if (parsed.docId)     p.id      = parsed.docId
    } else {
      p.search = rawSearch
    }
  }
  if (docTypeFilter.value)         p.document_type  = docTypeFilter.value
  if (projectStatusFilter.value)   p.project_status = projectStatusFilter.value
  if (isSignedFilter.value !== '') p.is_signed       = isSignedFilter.value
  return p
}

async function load() {
  loading.value  = true
  loadError.value = null
  try {
    const res = await getDocuments(buildParams())
    documents.value  = res.results ?? res ?? []
    totalCount.value = res.count ?? documents.value.length
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('documents.failedToLoad')
  } finally {
    loading.value = false
  }
}

// Reset to page 1 whenever any filter changes, then reload
watch([search, docTypeFilter, projectStatusFilter, isSignedFilter, ordering], () => {
  currentPage.value = 1
})
watch([search, docTypeFilter, projectStatusFilter, isSignedFilter, ordering, currentPage], load)

onMounted(load)

function confirmDelete(doc) {
  deleteTarget.value = doc
}

async function doDelete() {
  if (!deleteTarget.value) return
  deleting.value = true
  try {
    await deleteDocument(deleteTarget.value.id)
    documents.value  = documents.value.filter((d) => d.id !== deleteTarget.value.id)
    totalCount.value = Math.max(0, totalCount.value - 1)
    deleteTarget.value = null
  } catch (e) {
    alert(e?.data?.detail ?? e?.message ?? t('documents.failedToDelete'))
  } finally {
    deleting.value = false
  }
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' })
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function docTypeIcon(type) {
  switch (type) {
    case 'signed_contract': return MdiFileSign
    case 'contract':        return MdiHandshake
    case 'picking_list':    return MdiClipboardList
    default:                return MdiFileDocument
  }
}

function docTypeIconClass(type) {
  switch (type) {
    case 'signed_contract': return 'text-emerald-400'
    case 'contract':        return 'text-blue-400'
    case 'picking_list':    return 'text-amber-400'
    default:                return 'text-slate-300'
  }
}
</script>
